import { Injectable } from "@angular/core";
import { ProfileService } from "./profile.service";
import { ApiService } from "./api.service";
import { PaperSize } from "./escpos-ticket";

// Owns the user's printer paper-size choice — a single global setting under
// the profile (not per-device). The first time `ensure()` runs without a
// stored value it opens the prompt; the choice persists across tabs and
// devices via the profile, and stays in effect until the user changes it
// from the profile preferences tab.
//
// The prompt is rendered declaratively at the app shell —
// <app-paper-size-prompt> binds [open]/[deviceName] to this service and
// emits (chosen) back. Holding the active resolver here lets callers
// `await ensure(device)` from anywhere without knowing about the dialog.

const PREF_GROUP = "printer";
const PREF_KEY = "paperSize";

@Injectable({ providedIn: "root" })
export class PaperSizeService {
  // App-shell binding: <app-paper-size-prompt> reads these.
  promptOpen = false;
  promptDeviceName = "";

  private resolver: ((size: PaperSize) => void) | null = null;

  constructor(
    private profServ: ProfileService,
    private api: ApiService,
  ) {}

  /** Saved size or null if the user hasn't picked yet. */
  getStored(): PaperSize | null {
    const v = this.profServ.latestProfile?.preferences?.[PREF_GROUP]?.[PREF_KEY];
    return v === "80mm" || v === "58mm" ? v : null;
  }

  /** Profile preferences tab binding — falls back to 80mm so the radio always
   *  reflects what the printer code will use if the user prints right now. */
  get current(): PaperSize {
    return this.getStored() || "80mm";
  }

  /**
   * Resolve the user's paper size — saved value if known, otherwise opens
   * the prompt and resolves once they pick. `device` is used only to label
   * the prompt with the printer's name; the saved value is global.
   */
  async ensure(device: any): Promise<PaperSize> {
    const saved = this.getStored();
    if (saved) return saved;
    return new Promise<PaperSize>((resolve) => {
      this.resolver = resolve;
      this.promptDeviceName = device?.productName || "";
      this.promptOpen = true;
    });
  }

  /** Called by <app-paper-size-prompt> when the user confirms a size. */
  async onChoice(size: PaperSize): Promise<void> {
    const resolver = this.resolver;
    this.promptOpen = false;
    this.resolver = null;
    this.promptDeviceName = "";
    await this.save(size);
    if (resolver) resolver(size);
  }

  /** Used by the profile preferences tab to change the saved value. */
  async setSize(size: PaperSize): Promise<void> {
    await this.save(size);
  }

  private async save(size: PaperSize): Promise<void> {
    // Mirror the optimistic-local-update pattern used elsewhere so reads
    // in the same tab hit the cache before the round-trip lands.
    const prof = this.profServ.latestProfile;
    if (prof) {
      if (!prof.preferences) prof.preferences = {};
      if (!prof.preferences[PREF_GROUP]) prof.preferences[PREF_GROUP] = {};
      prof.preferences[PREF_GROUP][PREF_KEY] = size;
    }

    await this.api.setPreference({
      prefGroup: PREF_GROUP,
      preference: PREF_KEY,
      prefValue: size,
    });
  }
}
